import React, { useState, useEffect, useMemo } from 'react';
import { View, Text, TextInput, ScrollView, TouchableOpacity, StyleSheet, Linking, useWindowDimensions } from 'react-native';
import { useAppTheme } from '../utils/useAppTheme';
import { toggleTheme, loadTheme } from '../utils/themeState';
import { Helmet } from 'react-helmet-async';
import { calculateGematria, getAvailableCiphers, getCipherCategories, getHebrewCiphers, getHebrewCipherCategories } from '../utils/calculator';
import { decodeCalculation } from '../utils/shareUtils';
import { decodeSharedCollection } from '../utils/researchStorage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import DownloadPage from './pages/DownloadPage';
import PrivacyPage from './pages/PrivacyPage';
import DateCalculatorPage from './pages/DateCalculatorPage';
import BlogPage from './pages/BlogPage';
import ComparePage from './pages/ComparePage';
import NumberPropertiesPage from './pages/NumberPropertiesPage';
import BibleMatchPage from './pages/BibleMatchPage';
import NotesPage from './pages/NotesPage';
import CipherChartPage from './pages/CipherChartPage';
import DOBCalculatorPage from './pages/DOBCalculatorPage';
import FamousPeoplePage from './pages/FamousPeoplePage';
import ResearchCollection from './components/ResearchCollection';
import { saveToResearch } from '../utils/researchStorage';
import HebrewKeyboard from './components/HebrewKeyboard';
import CustomCipherModal from './components/CustomCipherModal';
import { getCustomCiphers, saveCustomCipher, deleteCustomCipher, generateCipherId } from '../utils/customCipherStorage.web';
import { isHebrewText } from '../data/hebrewCiphers';
import HomepageContent from './components/HomepageContent';
import WebNumberMeaning from './components/WebNumberMeaning';

export default function WebCalculator() {
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const { isDark, colors } = useAppTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);

  // Load persisted theme on mount
  useEffect(() => { loadTheme(); }, []);

  // Keep the browser canvas and overscroll area aligned with the active theme.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    const root = document.documentElement;
    const body = document.body;
    const previousRootStyles = {
      backgroundColor: root.style.backgroundColor,
      color: root.style.color,
      colorScheme: root.style.colorScheme,
    };
    const previousBodyStyles = {
      backgroundColor: body.style.backgroundColor,
      color: body.style.color,
      colorScheme: body.style.colorScheme,
    };
    const colorScheme = isDark ? 'dark' : 'light';

    root.style.backgroundColor = colors.background;
    root.style.color = colors.text;
    root.style.colorScheme = colorScheme;
    body.style.backgroundColor = colors.background;
    body.style.color = colors.text;
    body.style.colorScheme = colorScheme;

    return () => {
      Object.assign(root.style, previousRootStyles);
      Object.assign(body.style, previousBodyStyles);
    };
  }, [colors.background, colors.text, isDark]);

  const [currentPage, setCurrentPage] = useState(() => {
    if (typeof window !== 'undefined') {
      const path = window.location.pathname.replace('/', '');
      if (['about', 'blog', 'contact', 'dates', 'compare', 'download', 'privacy', 'research', 'number', 'bible', 'notes', 'cipher-chart', 'birthday', 'famous-people'].includes(path)) {
        return path;
      }
    }
    return 'home';
  });
  const [inputText, setInputText] = useState('');
  const [selectedCiphers, setSelectedCiphers] = useState({});
  const [results, setResults] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [collapsedCategories, setCollapsedCategories] = useState({});
  const [allCiphers, setAllCiphers] = useState([]);
  const [comparePhraseA, setComparePhraseA] = useState('');
  const [comparePhraseB, setComparePhraseB] = useState('');
  const [categories, setCategories] = useState({});
  const [showCipherFilters, setShowCipherFilters] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [targetNumber, setTargetNumber] = useState('');
  const [isHebrewMode, setIsHebrewMode] = useState(false);
  const [showHebrewKeyboard, setShowHebrewKeyboard] = useState(false);
  const [customCiphers, setCustomCiphers] = useState({});
  const [showCustomCipherModal, setShowCustomCipherModal] = useState(false);
  const [editingCipher, setEditingCipher] = useState(null);
  const [showAppBanner, setShowAppBanner] = useState(true);

  // Initialize ciphers and categories
  useEffect(() => {
    loadCiphers();
  }, [isHebrewMode]);

  const loadCiphers = async () => {
    // Load custom ciphers
    const allCustomCiphers = await getCustomCiphers();
    
    // Filter custom ciphers by language mode
    const filteredCustomCiphers = {};
    Object.keys(allCustomCiphers).forEach(key => {
      const cipher = allCustomCiphers[key];
      if (cipher.isHebrewCipher === isHebrewMode) {
        filteredCustomCiphers[key] = cipher;
      }
    });
    setCustomCiphers(filteredCustomCiphers);

    if (isHebrewMode) {
      // Load Hebrew ciphers
      const hebrewCiphersObj = getHebrewCiphers();
      const hebrewCats = getHebrewCipherCategories();

      const ciphersArray = Object.keys(hebrewCiphersObj).map(key => ({
        key: key,
        name: hebrewCiphersObj[key].name,
        nameHebrew: hebrewCiphersObj[key].nameHebrew,
        description: hebrewCiphersObj[key].description,
        category: hebrewCiphersObj[key].category
      }));

      // Add custom ciphers to array
      Object.keys(filteredCustomCiphers).forEach(key => {
        ciphersArray.push({
          key: key,
          name: filteredCustomCiphers[key].name,
          nameHebrew: filteredCustomCiphers[key].nameHebrew,
          description: filteredCustomCiphers[key].description,
          category: 'Custom',
          isCustom: true
        });
      });

      setAllCiphers(ciphersArray);

      const categoriesFormatted = {};
      Object.keys(hebrewCats).forEach(category => {
        categoriesFormatted[category] = hebrewCats[category].map(cipherKey => {
          const cipher = hebrewCiphersObj[cipherKey];
          return {
            key: cipherKey,
            name: cipher.name,
            nameHebrew: cipher.nameHebrew,
            category: category
          };
        });
      });

      // Add Custom category if there are custom ciphers
      if (Object.keys(filteredCustomCiphers).length > 0) {
        categoriesFormatted.Custom = Object.keys(filteredCustomCiphers).map(key => ({
          key: key,
          name: filteredCustomCiphers[key].name,
          nameHebrew: filteredCustomCiphers[key].nameHebrew,
          category: 'Custom',
          isCustom: true
        }));
      }

      setCategories(categoriesFormatted);

      // Select all Hebrew ciphers by default
      const initial = {};
      ciphersArray.forEach(cipher => {
        initial[cipher.name] = true;
        initial[cipher.key] = true;
      });
      setSelectedCiphers(initial);
    } else {
      // Load English ciphers
      const ciphersObj = getAvailableCiphers();
      const cats = getCipherCategories();

      const ciphersArray = Object.keys(ciphersObj).map(key => ({
        key: key,
        ...ciphersObj[key]
      }));

      // Add custom ciphers to array
      Object.keys(filteredCustomCiphers).forEach(key => {
        ciphersArray.push({
          key: key,
          name: filteredCustomCiphers[key].name,
          description: filteredCustomCiphers[key].description,
          category: 'Custom',
          isCustom: true
        });
      });

      setAllCiphers(ciphersArray);

      const categoriesFormatted = {};
      Object.keys(cats).forEach(category => {
        categoriesFormatted[category] = cats[category].map(cipherKey => {
          const cipher = ciphersObj[cipherKey];
          return {
            key: cipherKey,
            name: cipher.name,
            category: category
          };
        });
      });

      // Add Custom category if there are custom ciphers
      if (Object.keys(filteredCustomCiphers).length > 0) {
        categoriesFormatted.Custom = Object.keys(filteredCustomCiphers).map(key => ({
          key: key,
          name: filteredCustomCiphers[key].name,
          category: 'Custom',
          isCustom: true
        }));
      }

      setCategories(categoriesFormatted);

      // Select all English ciphers by default
      const initial = {};
      ciphersArray.forEach(cipher => {
        initial[cipher.name] = true;
      });
      setSelectedCiphers(initial);
    }
  };

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname.replace('/', '');
      if (['about', 'blog', 'contact', 'dates', 'compare', 'download', 'privacy', 'research', 'number', 'bible', 'notes', 'cipher-chart', 'birthday', 'famous-people'].includes(path)) {
        setCurrentPage(path);
      } else {
        setCurrentPage('home');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Update URL when page changes (without reloading)
  useEffect(() => {
    const path = currentPage === 'home' ? '/' : `/${currentPage}`;
    if (window.location.pathname !== path) {
      window.history.pushState({}, '', path + window.location.search);
    }
  }, [currentPage]);

  // Check for shared calculation in URL - run after ciphers are loaded
  useEffect(() => {
    if (allCiphers.length > 0) {
      handleSharedCalculation();
    }
  }, [allCiphers]);

  const importProcessed = React.useRef(false);

  // Handle incoming shared calculations
  const handleSharedCalculation = async () => {
    if (importProcessed.current) return;

    try {
      const urlParams = new URLSearchParams(window.location.search);
      const calcParam = urlParams.get('calc');
      const collectionParam = urlParams.get('collection');

      console.log('Checking URL params:', { calcParam, collectionParam });

      // If no valid params, don't mark as processed so that if they appear later we handle them?
      // Actually, if they aren't there on load, they won't appear later without a navigation event.
      if (!calcParam && !collectionParam) {
        return;
      }

      importProcessed.current = true;

      if (calcParam) {
        const decoded = decodeCalculation(calcParam);
        if (decoded && decoded.text) {
          setInputText(decoded.text);

          // Update selected ciphers if provided
          if (decoded.ciphers && decoded.ciphers.length > 0) {
            const newSelection = {};
            allCiphers.forEach(cipher => {
              newSelection[cipher.name] = decoded.ciphers.includes(cipher.name);
            });
            setSelectedCiphers(newSelection);
          }
        }
      } else if (collectionParam) {
        console.log('Processing collection parameter...');

        // Handle shared collection - import and show research page
        const entries = decodeSharedCollection(collectionParam);
        console.log('Decoded entries:', entries);

        if (entries && entries.length > 0) {
          const { importSharedCollection } = require('../utils/researchStorage');
          const importCount = await importSharedCollection(entries);

          if (importCount > 0) {
            alert(`Successfully imported ${importCount} research entries!`);
            console.log('Navigating to research page...');
            // Navigate to research page
            setCurrentPage('research');

            // Clear URL parameters to prevent re-import on refresh
            window.history.replaceState({}, document.title, window.location.pathname);
          } else {
            console.log('No new entries imported (all may be duplicates)');
            setCurrentPage('research');
            window.history.replaceState({}, document.title, window.location.pathname);
          }
        }
      }
    } catch (error) {
      console.error('Error loading shared calculation:', error);
      importProcessed.current = false; // Reset if error so user can retry? No, probably better to leave safely locked.
    }
  };

  const handleLoadResearch = (text, ciphers) => {
    setInputText(text);
    setSelectedCiphers(ciphers);
    setCurrentPage('home'); // Go back to home page
  };

  const handleLoadCompare = (phraseA, phraseB) => {
    setComparePhraseA(phraseA);
    setComparePhraseB(phraseB);
    setCurrentPage('compare');
  };

  const handleSaveToResearch = async () => {
    if (!inputText.trim()) {
      alert('Please enter some text to save');
      return;
    }

    // Make sure we have results calculated
    if (results.length === 0) {
      alert('Please wait for calculations to complete');
      return;
    }

    try {
      console.log('Saving to research:', {
        text: inputText,
        ciphers: selectedCiphers,
        resultsCount: results.length,
        sampleResult: results[0]
      });

      await saveToResearch(inputText, selectedCiphers, results, '', []);
      alert('Saved to Research List!');
    } catch (error) {
      alert(error.message);
    }
  };

  // Calculate results when input or selected ciphers change
  useEffect(() => {
    const calculateResults = async () => {
      if (inputText.trim() && allCiphers.length > 0) {
        const calculatedResults = await calculateGematria(inputText, selectedCiphers, isHebrewMode);
        setResults(calculatedResults);
      } else {
        setResults([]);
      }
    };
    
    calculateResults();
  }, [inputText, selectedCiphers, allCiphers, isHebrewMode]);

  const toggleCipher = (cipherName) => {
    setSelectedCiphers(prev => ({
      ...prev,
      [cipherName]: !prev[cipherName]
    }));
  };

  const toggleHebrewMode = () => {
    setIsHebrewMode(prev => !prev);
    setInputText(''); // Clear input when switching modes
    setResults([]);
  };

  const handleHebrewKeyPress = (key) => {
    if (key === 'backspace') {
      setInputText(prev => prev.slice(0, -1));
    } else {
      setInputText(prev => prev + key);
    }
  };

  const handleCreateCustomCipher = () => {
    setEditingCipher(null);
    setShowCustomCipherModal(true);
  };

  const handleEditCustomCipher = (cipherKey) => {
    const cipher = customCiphers[cipherKey];
    setEditingCipher({ ...cipher, key: cipherKey });
    setShowCustomCipherModal(true);
  };

  const handleSaveCustomCipher = async (cipherData) => {
    const cipherId = editingCipher?.key || generateCipherId(cipherData.name);
    const success = await saveCustomCipher(cipherId, cipherData);
    
    if (success) {
      await loadCiphers();
      // Auto-select the new/edited cipher
      setSelectedCiphers(prev => ({
        ...prev,
        [cipherId]: true,
        [cipherData.name]: true
      }));
      alert(editingCipher ? 'Cipher updated!' : 'Custom cipher created!');
    } else {
      alert('Failed to save cipher');
    }
  };

  const handleDeleteCustomCipher = async (cipherKey) => {
    if (confirm('Are you sure you want to delete this custom cipher?')) {
      const success = await deleteCustomCipher(cipherKey);
      if (success) {
        await loadCiphers();
        // Deselect the deleted cipher
        setSelectedCiphers(prev => {
          const newSelections = { ...prev };
          delete newSelections[cipherKey];
          return newSelections;
        });
        alert('Cipher deleted');
      } else {
        alert('Failed to delete cipher');
      }
    }
  };

  const selectAll = () => {
    const all = {};
    allCiphers.forEach(cipher => {
      all[cipher.name] = true;
    });
    setSelectedCiphers(all);
  };

  const clearAll = () => {
    const none = {};
    allCiphers.forEach(cipher => {
      none[cipher.name] = false;
    });
    setSelectedCiphers(none);
  };

  const toggleCategory = (category) => {
    setCollapsedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Filter ciphers based on search
  const filteredCiphers = Object.keys(categories).reduce((acc, category) => {
    const filtered = categories[category].filter(cipher =>
      cipher.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      category.toLowerCase().includes(searchTerm.toLowerCase())
    );
    if (filtered.length > 0) {
      acc[category] = filtered;
    }
    return acc;
  }, {});

  // Filter results by target number if specified
  const filteredResults = targetNumber.trim()
    ? results.filter(result => result.totalValue.toString() === targetNumber.trim())
    : results;

  // Group results by category - ensure filteredResults is an array
  const groupedResults = Array.isArray(filteredResults) ? filteredResults.reduce((acc, result) => {
    const category = result.category;
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(result);
    return acc;
  }, {}) : {};

  const openPrivacyPolicy = () => {
    window.location.href = '/privacy';
  };

  const openTerms = () => {
    window.location.href = '/terms';
  };

  // Render different pages based on currentPage state
  const renderPage = () => {
    switch (currentPage) {
      case 'research':
        return (
          <View style={[styles.fullPageContent, isMobile && styles.fullPageContentMobile]}>
            <ResearchCollection
              currentText={inputText}
              currentCiphers={selectedCiphers}
              currentResults={results}
              onLoadResearch={handleLoadResearch}
              onLoadCompare={handleLoadCompare}
            />
          </View>
        );
      case 'about':
        return <AboutPage />;
      case 'blog':
        return <BlogPage />;
      case 'contact':
        return <ContactPage />;
      case 'dates':
        return <DateCalculatorPage />;
      case 'number':
        return <NumberPropertiesPage />;
      case 'bible':
        return <BibleMatchPage selectedCiphers={selectedCiphers} />;
      case 'compare':
        return <ComparePage loadedPhraseA={comparePhraseA} loadedPhraseB={comparePhraseB} />;
      case 'download':
        return <DownloadPage />;
      case 'privacy':
        return <PrivacyPage />;
      case 'notes':
        return <NotesPage />;
      case 'cipher-chart':
        return <CipherChartPage />;
      case 'birthday':
        return <DOBCalculatorPage />;
      case 'famous-people':
        return <FamousPeoplePage />;
      default:
        return renderHomePage();
    }
  };

  // Helper to render the cipher filter sidebar content
  const renderCipherFiltersContent = () => (
    <>
      <Text style={styles.sidebarTitle}>Filter Ciphers</Text>

      {/* Create Custom Cipher Button */}
      <TouchableOpacity
        style={styles.createCustomButton}
        onPress={handleCreateCustomCipher}
      >
        <Text style={styles.createCustomButtonText}>+ Create Custom Cipher</Text>
      </TouchableOpacity>

      <TextInput
        style={styles.searchInput}
        placeholder="Search ciphers..."
        placeholderTextColor={colors.textSecondary}
        value={searchTerm}
        onChangeText={setSearchTerm}
      />

      <View style={styles.filterActions}>
        <TouchableOpacity style={styles.actionButton} onPress={selectAll}>
          <Text style={styles.actionButtonText}>Select All</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.actionButton} onPress={clearAll}>
          <Text style={styles.actionButtonText}>Clear All</Text>
        </TouchableOpacity>
      </View>

      <ScrollView style={isMobile ? { maxHeight: 400 } : styles.cipherList}>
        {Object.keys(filteredCiphers).map(category => (
          <View key={category}>
            <TouchableOpacity
              style={styles.categoryHeader}
              onPress={() => toggleCategory(category)}
            >
              <Text style={styles.categoryHeaderText}>{category}</Text>
              <Text style={styles.categoryArrow}>
                {collapsedCategories[category] ? '▶' : '▼'}
              </Text>
            </TouchableOpacity>

            {!collapsedCategories[category] && filteredCiphers[category].map(cipher => (
              <View key={cipher.name} style={styles.cipherItemWrapper}>
                <TouchableOpacity
                  style={styles.cipherItem}
                  onPress={() => toggleCipher(cipher.name)}
                >
                  <View style={[
                    styles.checkbox,
                    selectedCiphers[cipher.name] && styles.checkboxChecked
                  ]}>
                    {selectedCiphers[cipher.name] && (
                      <Text style={styles.checkmark}>✓</Text>
                    )}
                  </View>
                  <View style={styles.cipherNameContainer}>
                    <Text style={styles.cipherName}>{cipher.name}</Text>
                    {cipher.nameHebrew && (
                      <Text style={styles.cipherNameHebrew}>{cipher.nameHebrew}</Text>
                    )}
                  </View>
                </TouchableOpacity>
                {cipher.isCustom && (
                  <View style={styles.customCipherActions}>
                    <TouchableOpacity
                      style={styles.editButton}
                      onPress={() => handleEditCustomCipher(cipher.key)}
                    >
                      <Text style={styles.actionIcon}>✏️</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.deleteButton}
                      onPress={() => handleDeleteCustomCipher(cipher.key)}
                    >
                      <Text style={styles.actionIcon}>🗑️</Text>
                    </TouchableOpacity>
                  </View>
                )}
              </View>
            ))}
          </View>
        ))}
      </ScrollView>
    </>
  );

  // Helper to render the summary sidebar content
  const renderSummaryContent = () => (
    <>
      <Text style={styles.sidebarTitle}>Summary</Text>
      <ScrollView style={isMobile ? { maxHeight: 400 } : styles.summaryList}>
        {results.length === 0 ? (
          <Text style={styles.emptyText}>Enter text to see summary</Text>
        ) : targetNumber.trim() && filteredResults.length === 0 ? (
          <Text style={styles.emptyText}>No matches for {targetNumber}</Text>
        ) : (
          Object.keys(groupedResults).map(category => (
            <View key={category}>
              <TouchableOpacity
                style={styles.summaryCategoryHeader}
                onPress={() => toggleCategory(`summary-${category}`)}
              >
                <Text style={styles.summaryCategoryText}>{category}</Text>
                <Text style={styles.categoryArrow}>
                  {collapsedCategories[`summary-${category}`] ? '▶' : '▼'}
                </Text>
              </TouchableOpacity>

              {!collapsedCategories[`summary-${category}`] && groupedResults[category].map((result, index) => (
                <View key={index} style={styles.summaryItem}>
                  <Text style={styles.summaryName}>{result.name}</Text>
                  <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <TouchableOpacity onPress={() => window.location.href = `/number?num=${result.totalValue}`}>
                      <Text style={[styles.summaryValue, {textDecorationLine: 'underline'}]}>{result.totalValue}</Text>
                    </TouchableOpacity>
                    <WebNumberMeaning number={result.totalValue} />
                  </View>
                </View>
              ))}
            </View>
          ))
        )}
      </ScrollView>
    </>
  );

  const renderHomePage = () => {
    return (
      <View style={[styles.mainContent, isMobile && styles.mainContentMobile]}>
        {/* Left Sidebar - Desktop only */}
        {!isMobile && (
          <View style={styles.leftSidebar}>
            {renderCipherFiltersContent()}
          </View>
        )}

        <View style={[styles.centerContent, isMobile && styles.centerContentMobile]}>
          <View style={styles.inputSection}>
            {/* Language Mode Toggle */}
            <View style={styles.languageModeContainer}>
              <TouchableOpacity
                style={[styles.languageToggle, !isHebrewMode && styles.languageToggleActive]}
                onPress={() => !isHebrewMode || toggleHebrewMode()}
              >
                <Text style={[styles.languageToggleText, !isHebrewMode && styles.languageToggleTextActive]}>
                  English
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.languageToggle, isHebrewMode && styles.languageToggleActive]}
                onPress={() => isHebrewMode || toggleHebrewMode()}
              >
                <Text style={[styles.languageToggleText, isHebrewMode && styles.languageToggleTextActive]}>
                  עברית (Hebrew)
                </Text>
              </TouchableOpacity>
            </View>

            <TextInput
              style={[
                styles.textInput,
                isHebrewMode && styles.textInputRTL
              ]}
              placeholder={isHebrewMode ? "הזן מילים בעברית בלבד... (Hebrew words only)" : "Enter text to calculate..."}
              placeholderTextColor={colors.textSecondary}
              value={inputText}
              onChangeText={setInputText}
              multiline
            />

            {/* Hebrew Keyboard Toggle Button */}
            {isHebrewMode && (
              <TouchableOpacity
                style={styles.keyboardToggle}
                onPress={() => setShowHebrewKeyboard(!showHebrewKeyboard)}
              >
                <Text style={styles.keyboardToggleText}>
                  {showHebrewKeyboard ? '⌨️ Hide Keyboard' : '⌨️ Show Hebrew Keyboard'}
                </Text>
              </TouchableOpacity>
            )}

            {/* Hebrew Keyboard */}
            {isHebrewMode && showHebrewKeyboard && (
              <HebrewKeyboard
                onKeyPress={handleHebrewKeyPress}
                visible={showHebrewKeyboard}
              />
            )}

            <TouchableOpacity
              style={styles.saveToResearchButton}
              onPress={handleSaveToResearch}
            >
              <Text style={styles.saveToResearchButtonText}>💾 Save to Research List</Text>
            </TouchableOpacity>

            <View style={styles.filterSection}>
              <Text style={styles.filterLabel}>Filter by Number (optional):</Text>
              <TextInput
                style={styles.numberInput}
                placeholder="e.g., 33"
                placeholderTextColor={colors.textSecondary}
                value={targetNumber}
                onChangeText={setTargetNumber}
                keyboardType="numeric"
              />
            </View>
          </View>

          {isMobile && (
            <View style={styles.mobileActions}>
              <TouchableOpacity
                style={[styles.actionButton, { marginBottom: 15 }]}
                onPress={() => setShowCipherFilters(!showCipherFilters)}
              >
                <Text style={styles.actionButtonText}>
                  {showCipherFilters ? 'Hide Filter' : 'Filter Ciphers'}
                </Text>
              </TouchableOpacity>

              {showCipherFilters && (
                <View style={[styles.leftSidebar, { width: '100%', marginBottom: 20 }]}>
                  {renderCipherFiltersContent()}
                </View>
              )}

              <TouchableOpacity
                style={[styles.actionButton, styles.summaryToggleButton, { marginBottom: 20 }]}
                onPress={() => setShowSummary(!showSummary)}
              >
                <Text style={[styles.actionButtonText, styles.summaryToggleButtonText]}>
                  {showSummary ? 'Hide Summary' : 'Show Summary'}
                </Text>
              </TouchableOpacity>

              {showSummary && (
                <View style={[styles.rightSidebar, { width: '100%', marginBottom: 20 }]}>
                  {renderSummaryContent()}
                </View>
              )}
            </View>
          )}

          <Text style={styles.resultsTitle}>Results</Text>
          <ScrollView style={styles.resultsSection}>
            {results.length === 0 ? (
              <Text style={styles.emptyText}>Enter text to see results</Text>
            ) : targetNumber.trim() && filteredResults.length === 0 ? (
              <View style={styles.noMatchContainer}>
                <Text style={styles.noMatchIcon}>🔍</Text>
                <Text style={styles.noMatchText}>No ciphers match the number {targetNumber}</Text>
                <Text style={styles.noMatchSubtext}>Try a different number or clear the filter</Text>
              </View>
            ) : (
              <View style={styles.resultsGrid}>
                {filteredResults.map((result, index) => (
                  <View key={index} style={styles.resultCard}>
                    <View style={styles.resultHeader}>
                      <View style={styles.resultNameContainer}>
                        <Text style={styles.resultCipherName}>{result.name}</Text>
                        {result.nameHebrew && (
                          <Text style={styles.resultCipherNameHebrew}>{result.nameHebrew}</Text>
                        )}
                      </View>
                      <Text style={styles.resultCategory}>{result.category}</Text>
                      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                        <TouchableOpacity onPress={() => window.location.href = `/number?num=${result.totalValue}`}>
                          <Text style={[styles.resultValue, {textDecorationLine: 'underline'}]}>{result.totalValue}</Text>
                        </TouchableOpacity>
                        <WebNumberMeaning number={result.totalValue} />
                      </View>
                    </View>

                    {result.description && (
                      <Text style={styles.cipherDescription}>{result.description}</Text>
                    )}

                    <View style={styles.wordBreakdown}>
                      <Text style={styles.wordBreakdownTitle}>Word Breakdown:</Text>
                      {result.wordValues && result.wordValues.map((wordObj, wordIndex) => (
                        <View key={wordIndex} style={styles.wordItem}>
                          <View style={styles.wordHeader}>
                            <Text style={styles.wordText}>{wordObj.word}</Text>
                            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                              <TouchableOpacity onPress={() => window.location.href = `/number?num=${wordObj.value}`}>
                                <Text style={[styles.wordValue, {textDecorationLine: 'underline'}]}>{wordObj.value}</Text>
                              </TouchableOpacity>
                              <WebNumberMeaning number={wordObj.value} />
                            </View>
                          </View>
                          {result.letterBreakdowns && result.letterBreakdowns[wordIndex] && (
                            <Text style={styles.letterBreakdown}>{result.letterBreakdowns[wordIndex]}</Text>
                          )}
                        </View>
                      ))}
                    </View>
                  </View>
                ))}
              </View>
            )}
          </ScrollView>
        </View>

        {/* Right Sidebar - Desktop only */}
        {!isMobile && (
          <View style={styles.rightSidebar}>
            {renderSummaryContent()}
          </View>
        )}
      </View>
    );
  };

  return (
    <>
      <Helmet>
        <title>Gematria Calculator - Calculate Hebrew & English Numerology Values</title>
        <meta name="description" content="Free online gematria calculator supporting 50+ cipher systems. Calculate Hebrew, English, and Greek numerology values instantly. Explore Kabbalah and sacred numerology." />
        <meta name="keywords" content="gematria calculator, hebrew numerology, english gematria, kabbalah calculator, numerology, cipher calculator, hebrew letters" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://gematriacalculator.xyz/" />
        <meta property="og:title" content="Gematria Calculator - Calculate Hebrew & English Numerology Values" />
        <meta property="og:description" content="Free online gematria calculator supporting 50+ cipher systems. Calculate Hebrew, English, and Greek numerology values instantly." />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://gematriacalculator.xyz/" />
        <meta property="twitter:title" content="Gematria Calculator - Calculate Hebrew & English Numerology Values" />
        <meta property="twitter:description" content="Free online gematria calculator supporting 50+ cipher systems. Calculate Hebrew, English, and Greek numerology values instantly." />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://gematriacalculator.xyz/" />
      </Helmet>

      {/* Custom Cipher Modal */}
      <CustomCipherModal
        visible={showCustomCipherModal}
        onClose={() => setShowCustomCipherModal(false)}
        onSave={handleSaveCustomCipher}
        editingCipher={editingCipher}
        isHebrewMode={isHebrewMode}
      />

      <View style={styles.container}>
      {/* Entire page is now scrollable, including Header and Footer */}
      <ScrollView contentContainerStyle={{ flexGrow: 1 }}>
        {/* Header with Navigation - Moved inside ScrollView */}
        <View style={[styles.header, isMobile && styles.headerMobile]}>
          <View style={styles.headerTopRow}>
            <TouchableOpacity onPress={() => {
              setCurrentPage('home');
              setShowCipherFilters(false);
              setShowSummary(false);
              setInputText('');
              setTargetNumber('');
            }}>
              <Text style={styles.headerTitle}>Gematria Calculator</Text>
            </TouchableOpacity>
            <TouchableOpacity
              onPress={toggleTheme}
              style={styles.themeToggle}
              accessibilityRole="switch"
              accessibilityLabel={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              accessibilityHint="Changes the website color theme"
              accessibilityState={{ checked: isDark }}
            >
              <Text style={styles.themeToggleText}>{isDark ? '☀️' : '🌙'}</Text>
            </TouchableOpacity>
          </View>
          <ScrollView 
            horizontal={isMobile} 
            showsHorizontalScrollIndicator={false}
            style={isMobile ? { width: '100%' } : {}}
            contentContainerStyle={isMobile ? { paddingHorizontal: 10 } : {}}
          >
            <View style={[styles.nav, isMobile && styles.navMobile]}>
              <TouchableOpacity onPress={() => {
                setCurrentPage('home');
                setShowCipherFilters(false);
                setShowSummary(false);
              }}>
                <Text style={[styles.navLink, currentPage === 'home' && styles.navLinkActive]}>Home</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => {
                setCurrentPage('dates');
                setShowCipherFilters(false);
                setShowSummary(false);
              }}>
                <Text style={[styles.navLink, currentPage === 'dates' && styles.navLinkActive]}>Dates</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => {
                setCurrentPage('number');
                setShowCipherFilters(false);
                setShowSummary(false);
              }}>
                <Text style={[styles.navLink, currentPage === 'number' && styles.navLinkActive]}>Number</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => {
                setCurrentPage('compare');
                setShowCipherFilters(false);
                setShowSummary(false);
              }}>
                <Text style={[styles.navLink, currentPage === 'compare' && styles.navLinkActive]}>Compare</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => {
                setCurrentPage('bible');
                setShowCipherFilters(false);
                setShowSummary(false);
              }}>
                <Text style={[styles.navLink, currentPage === 'bible' && styles.navLinkActive]}>Bible</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => {
                setCurrentPage('research');
                setShowCipherFilters(false);
                setShowSummary(false);
              }}>
                <Text style={[styles.navLink, currentPage === 'research' && styles.navLinkActive]}>Research</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => {
                setCurrentPage('notes');
                setShowCipherFilters(false);
                setShowSummary(false);
              }}>
                <Text style={[styles.navLink, currentPage === 'notes' && styles.navLinkActive]}>Notes</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => {
                setCurrentPage('birthday');
                setShowCipherFilters(false);
                setShowSummary(false);
              }}>
                <Text style={[styles.navLink, currentPage === 'birthday' && styles.navLinkActive]}>Birthday</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => {
                setCurrentPage('famous-people');
                setShowCipherFilters(false);
                setShowSummary(false);
              }}>
                <Text style={[styles.navLink, currentPage === 'famous-people' && styles.navLinkActive]}>Famous People</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => {
                setCurrentPage('cipher-chart');
                setShowCipherFilters(false);
                setShowSummary(false);
              }}>
                <Text style={[styles.navLink, currentPage === 'cipher-chart' && styles.navLinkActive]}>Chart</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => {
                window.location.href = '/contact';
              }}>
                <Text style={[styles.navLink, currentPage === 'contact' && styles.navLinkActive]}>Contact</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => {
                setCurrentPage('download');
                setShowCipherFilters(false);
                setShowSummary(false);
              }}>
                <Text style={[styles.navLink, currentPage === 'download' && styles.navLinkActive]}>App</Text>
              </TouchableOpacity>
            </View>
          </ScrollView>
        </View>

        {/* Mobile App Banner */}
        {showAppBanner && (
          <View style={[styles.appBanner, isMobile && styles.appBannerMobile]}>
            <Text style={styles.appBannerText}>📱 Download our mobile app</Text>
            <View style={styles.appBannerButtons}>
              <TouchableOpacity
                style={styles.appStoreButton}
                onPress={() => Linking.openURL('https://apps.apple.com/us/app/gematria-calculator-decode/id6744337544')}
              >
                <Text style={styles.appStoreButtonText}>App Store</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.appStoreButton}
                onPress={() => Linking.openURL('https://play.google.com/store/apps/details?id=com.gematriacalculator.gematriacalculator')}
              >
                <Text style={styles.appStoreButtonText}>Google Play</Text>
              </TouchableOpacity>
            </View>
            <TouchableOpacity
              style={styles.closeBannerButton}
              onPress={() => setShowAppBanner(false)}
            >
              <Text style={styles.closeBannerButtonText}>✕</Text>
            </TouchableOpacity>
          </View>
        )}

        {/* Page Content */}
        {renderPage()}

        {/* Homepage Content Section - for SEO and user reference */}
        {currentPage === 'home' && <HomepageContent isMobile={isMobile} />}

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>Gematria Calculator © 2025</Text>
          <View style={styles.footerLinks}>
            <TouchableOpacity onPress={openPrivacyPolicy}>
              <Text style={styles.footerLink}>Privacy Policy</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={openTerms}>
              <Text style={styles.footerLink}>Terms & Conditions</Text>
            </TouchableOpacity>
          </View>
        </View>
      </ScrollView>
    </View>
    </>
  );
}

const createStyles = (colors) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  header: {
    backgroundColor: colors.secondary,
    padding: 12,
    alignItems: 'center',
  },
  headerTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    position: 'relative',
  },
  themeToggle: {
    position: 'absolute',
    right: 10,
    padding: 6,
    borderRadius: 20,
    backgroundColor: colors.surfaceElevated,
    borderWidth: 1,
    borderColor: colors.border,
  },
  themeToggleText: {
    fontSize: 18,
  },
  headerTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    color: colors.onSecondary,
    marginBottom: 8,
  },
  appBanner: {
    backgroundColor: colors.surface,
    paddingVertical: 8,
    paddingHorizontal: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 15,
    borderBottomWidth: 1,
    borderBottomColor: colors.divider,
  },
  appBannerMobile: {
    flexDirection: 'column',
    gap: 8,
    paddingVertical: 10,
  },
  appBannerText: {
    color: colors.textSecondary,
    fontSize: 14,
    fontWeight: '500',
  },
  appBannerButtons: {
    flexDirection: 'row',
    gap: 10,
  },
  appStoreButton: {
    backgroundColor: '#007AFF',
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 4,
  },
  appStoreButtonText: {
    color: colors.onPrimary,
    fontSize: 13,
    fontWeight: '500',
  },
  closeBannerButton: {
    position: 'absolute',
    right: 10,
    top: '50%',
    transform: [{ translateY: -12 }],
    padding: 4,
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeBannerButtonText: {
    color: colors.textSecondary,
    fontSize: 18,
    fontWeight: 'bold',
    lineHeight: 18,
  },
  nav: {
    flexDirection: 'row',
    gap: 20,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  navMobile: {
    gap: 10,
    borderTopWidth: 0,
    paddingTop: 0,
  },
  navLink: {
    color: colors.onSecondary,
    fontSize: 14,
    fontWeight: '500',
    paddingVertical: 5,
    paddingHorizontal: 8,
  },
  navLinkActive: {
    color: colors.onPrimary,
    backgroundColor: colors.primary,
    borderRadius: 3,
  },
  mainContent: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
    padding: 20,
    gap: 20,
  },
  mainContentMobile: {
    flexDirection: 'column',
    padding: 10,
    gap: 15,
  },
  fullPageContent: {
    flex: 1,
    padding: 20,
    maxWidth: 1400,
    alignSelf: 'center',
    width: '100%',
  },
  fullPageContentMobile: {
    padding: 0,
  },
  leftSidebar: {
    width: 250,
    backgroundColor: colors.surface,
    borderRadius: 5,
    padding: 15,
    shadowColor: colors.shadow,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 10,
    elevation: 3,
  },
  rightSidebar: {
    width: 300,
    backgroundColor: colors.surface,
    borderRadius: 5,
    padding: 15,
    shadowColor: colors.shadow,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 10,
    elevation: 3,
  },
  sidebarTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: colors.text,
    marginBottom: 15,
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: colors.divider,
  },
  searchInput: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 4,
    padding: 8,
    marginBottom: 15,
    fontSize: 14,
    color: colors.text,
    backgroundColor: colors.inputBackground,
  },
  createCustomButton: {
    backgroundColor: colors.secondary,
    padding: 10,
    borderRadius: 5,
    alignItems: 'center',
    marginBottom: 15,
  },
  createCustomButtonText: {
    color: colors.onSecondary,
    fontSize: 14,
    fontWeight: '600',
  },
  cipherItemWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  customCipherActions: {
    flexDirection: 'row',
    gap: 5,
    marginLeft: 5,
  },
  editButton: {
    padding: 5,
    backgroundColor: colors.primarySurface,
    borderRadius: 4,
  },
  deleteButton: {
    padding: 5,
    backgroundColor: colors.dangerSurface,
    borderRadius: 4,
  },
  actionIcon: {
    fontSize: 16,
  },
  filterActions: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 15,
  },
  actionButton: {
    flex: 1,
    backgroundColor: colors.primary,
    padding: 8,
    borderRadius: 5,
    alignItems: 'center',
  },
  summaryToggleButton: {
    backgroundColor: colors.secondary,
  },
  summaryToggleButtonText: {
    color: colors.onSecondary,
  },
  actionButtonText: {
    color: colors.onPrimary,
    fontSize: 14,
    fontWeight: '500',
  },
  cipherList: {
    flex: 1,
  },
  categoryHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: colors.primarySurface,
    padding: 8,
    borderRadius: 4,
    borderLeftWidth: 3,
    borderLeftColor: colors.primary,
    marginTop: 15,
    marginBottom: 5,
  },
  categoryHeaderText: {
    fontWeight: 'bold',
    fontSize: 15,
    color: colors.text,
  },
  categoryArrow: {
    fontSize: 10,
    color: colors.textSecondary,
  },
  cipherItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 8,
    marginLeft: 10,
    borderRadius: 4,
  },
  checkbox: {
    width: 18,
    height: 18,
    borderWidth: 2,
    borderColor: colors.primary,
    borderRadius: 3,
    marginRight: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxChecked: {
    backgroundColor: colors.primary,
  },
  checkmark: {
    color: colors.onPrimary,
    fontSize: 12,
    fontWeight: 'bold',
  },
  cipherName: {
    fontSize: 14,
    color: colors.text,
  },
  cipherNameContainer: {
    flex: 1,
  },
  cipherNameHebrew: {
    fontSize: 12,
    color: colors.textSecondary,
    marginTop: 2,
    textAlign: 'right',
  },
  centerContent: {
    width: 550,
    backgroundColor: colors.surfaceElevated,
    borderRadius: 5,
    padding: 20,
    shadowColor: colors.shadow,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 10,
    elevation: 3,
    minHeight: 500,
  },
  centerContentMobile: {
    width: '100%',
    padding: 15,
  },
  mobileActions: {
    width: '100%',
    marginVertical: 10,
  },
  inputSection: {
    marginBottom: 20,
  },
  languageModeContainer: {
    flexDirection: 'row',
    marginBottom: 15,
    borderRadius: 8,
    overflow: 'hidden',
    borderWidth: 2,
    borderColor: colors.primary,
  },
  languageToggle: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 20,
    backgroundColor: colors.mutedSurface,
    alignItems: 'center',
    justifyContent: 'center',
  },
  languageToggleActive: {
    backgroundColor: colors.primary,
  },
  languageToggleText: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.textSecondary,
  },
  languageToggleTextActive: {
    color: colors.onPrimary,
  },
  textInput: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 5,
    padding: 10,
    fontSize: 16,
    minHeight: 100,
    textAlignVertical: 'top',
    marginBottom: 10,
    color: colors.text,
    backgroundColor: colors.inputBackground,
  },
  textInputRTL: {
    textAlign: 'right',
    writingDirection: 'rtl',
  },
  keyboardToggle: {
    backgroundColor: colors.secondary,
    paddingVertical: 10,
    paddingHorizontal: 15,
    borderRadius: 5,
    alignItems: 'center',
    marginBottom: 10,
  },
  keyboardToggleText: {
    color: colors.onSecondary,
    fontSize: 14,
    fontWeight: '600',
  },
  saveToResearchButton: {
    backgroundColor: colors.primary,
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 5,
    alignItems: 'center',
  },
  saveToResearchButtonText: {
    color: colors.onPrimary,
    fontSize: 16,
    fontWeight: '600',
  },
  filterSection: {
    marginTop: 15,
    paddingTop: 15,
    borderTopWidth: 1,
    borderTopColor: colors.divider,
  },
  filterLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.text,
    marginBottom: 8,
  },
  numberInput: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 5,
    padding: 10,
    fontSize: 16,
    color: colors.text,
    backgroundColor: colors.inputBackground,
  },
  noMatchContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60,
    paddingHorizontal: 20,
    backgroundColor: colors.warningSurface,
    borderRadius: 5,
  },
  noMatchIcon: {
    fontSize: 48,
    marginBottom: 15,
  },
  noMatchText: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.warning,
    marginBottom: 8,
  },
  noMatchSubtext: {
    fontSize: 14,
    color: colors.textSecondary,
    textAlign: 'center',
  },
  resultsTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: colors.text,
    marginBottom: 15,
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: colors.divider,
  },
  resultsSection: {
    flex: 1,
  },
  resultsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 20,
  },
  resultCard: {
    width: '100%',
    backgroundColor: colors.mutedSurface,
    borderRadius: 5,
    borderLeftWidth: 4,
    borderLeftColor: colors.primary,
    padding: 15,
    marginBottom: 15,
  },
  resultHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
    flexWrap: 'wrap',
  },
  resultNameContainer: {
    flex: 1,
    minWidth: 150,
  },
  resultCipherName: {
    fontWeight: 'bold',
    fontSize: 16,
    color: colors.text,
  },
  resultCipherNameHebrew: {
    fontSize: 13,
    color: colors.textSecondary,
    marginTop: 2,
    textAlign: 'right',
  },
  resultCategory: {
    backgroundColor: colors.primary,
    color: colors.onPrimary,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 12,
    fontSize: 12,
    marginHorizontal: 10,
  },
  resultValue: {
    fontSize: 18,
    fontWeight: 'bold',
    color: colors.primary,
    minWidth: 60,
    textAlign: 'right',
  },
  cipherDescription: {
    color: colors.textSecondary,
    fontSize: 12,
    marginBottom: 10,
    padding: 6,
    backgroundColor: colors.surface,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: colors.divider,
  },
  wordBreakdown: {
    marginTop: 10,
  },
  wordBreakdownTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 8,
    color: colors.text,
  },
  wordItem: {
    backgroundColor: colors.primarySurface,
    padding: 8,
    marginBottom: 8,
    borderRadius: 4,
  },
  wordHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  wordText: {
    fontWeight: 'bold',
    color: colors.text,
  },
  wordValue: {
    color: colors.primary,
    fontWeight: 'bold',
  },
  letterBreakdown: {
    fontSize: 11,
    color: colors.textSecondary,
  },
  summaryList: {
    flex: 1,
  },
  summaryCategoryHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: colors.primarySurface,
    padding: 8,
    borderRadius: 4,
    borderLeftWidth: 3,
    borderLeftColor: colors.primary,
    marginTop: 15,
    marginBottom: 5,
  },
  summaryCategoryText: {
    fontWeight: 'bold',
    fontSize: 14,
    color: colors.text,
  },
  summaryItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 8,
    marginLeft: 10,
    backgroundColor: colors.mutedSurface,
    borderRadius: 4,
    borderLeftWidth: 3,
    borderLeftColor: colors.primary,
    marginBottom: 8,
  },
  summaryName: {
    fontSize: 14,
    fontWeight: '500',
    color: colors.text,
  },
  summaryValue: {
    fontSize: 14,
    fontWeight: 'bold',
    color: colors.primary,
  },
  emptyText: {
    color: colors.textSecondary,
    fontStyle: 'italic',
    textAlign: 'center',
    marginTop: 20,
  },
  footer: {
    backgroundColor: colors.secondary,
    padding: 15,
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
  footerText: {
    color: colors.onSecondary,
    opacity: 0.75,
    fontSize: 14,
    marginBottom: 8,
    textAlign: 'center',
  },
  footerLinks: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 20,
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    width: '100%',
    maxWidth: 600,
  },
  footerLink: {
    color: colors.onSecondary,
    fontSize: 14,
    fontWeight: '500',
    textAlign: 'center',
  },
});
